import React, { useEffect, useState } from 'react';
import { Box, Button, TextField, CircularProgress, Select, MenuItem, FormControl, InputLabel, InputAdornment, IconButton } from '@mui/material';
import BadgeIcon from '@mui/icons-material/Badge';
import SchoolIcon from '@mui/icons-material/School';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import ClearIcon from '@mui/icons-material/Clear';
import CardPerimeterSchool from './_components/CardPerimeterSchool';
import { getStreamByEndpointAsync } from 'services/Events';
import { transformDateStringInDate } from 'utils/TransformUtils';
import { IPersonPerimeter } from 'services/SchoolPerimeterService/types';
import { SCHOOL_MONITORING_EVENT_ENDPOINT } from 'services/Events/SchoolMonitoringEventService';
import iconSchool from 'assets/images/icons/icons8-escola-64.png';
import iconOutSideSchool from 'assets/images/icons/icons8-leave-house-100.png';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getSchoolPerimeterStatusAsync } from 'services/SchoolPerimeterService';

type PersonType = 'Student' | 'Responsible' | 'General';
type SortOption = 'nameAsc' | 'nameDesc' | 'timeDesc' | 'timeAsc';

const MonitoringSchoolPerimeterPages = () => {
  const [lastEvent, setLastEvent] = useState<any>();
  const [events, setEvents] = useState<any[]>([]);
  const [peopleInside, setPeopleInside] = useState<IPersonPerimeter[]>([]);
  const [peopleOutside, setPeopleOutside] = useState<IPersonPerimeter[]>([]);
  const [filterName, setFilterName] = useState('');
  const [selectedTypes, setSelectedTypes] = useState<PersonType[]>([]);
  const [sortBy, setSortBy] = useState<SortOption>('nameAsc');
  const [isLoading, setIsLoading] = useState(false);
  const [filterFocusState, setFilterFocusState] = useState<'default' | 'hover' | 'focused'>('default');
  const [isClearing, setIsClearing] = useState(false);

  const loadPerimeterStatus = async () => {
    setIsLoading(true);
    try {
      const discriminator = selectedTypes.length > 0 ? selectedTypes.join(',') : undefined;

      const response = await getSchoolPerimeterStatusAsync({
        search: filterName || undefined,
        discriminator,
      });

      setPeopleInside(filterAndSortPeople(response.perimeter.inside));
      setPeopleOutside(filterAndSortPeople(response.perimeter.outside));
    } catch (error: any) {
      console.error('Erro ao carregar perímetro:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filterAndSortPeople = (people: IPersonPerimeter[]) => {
    const filtered = [...people];

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'nameAsc':
          return a.name.localeCompare(b.name);
        case 'nameDesc':
          return b.name.localeCompare(a.name);
        case 'timeDesc':
          return (b.lastEventTime ? new Date(b.lastEventTime).getTime() : 0) -
            (a.lastEventTime ? new Date(a.lastEventTime).getTime() : 0);
        case 'timeAsc':
          return (a.lastEventTime ? new Date(a.lastEventTime).getTime() : 0) -
            (b.lastEventTime ? new Date(b.lastEventTime).getTime() : 0);
        default:
          return 0;
      }
    });

    return filtered;
  };

  const toggleTypeFilter = (type: PersonType) => {
    setSelectedTypes((prev) => {
      if (prev.includes(type)) {
        return prev.filter((t) => t !== type);
      } else {
        return [...prev, type];
      }
    });
  };

  useEffect(() => {
    loadPerimeterStatus();
  }, []);

  useEffect(() => {
    loadPerimeterStatus();
  }, [selectedTypes, sortBy]);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadPerimeterStatus();
    }, 500);
    return () => clearTimeout(timer);
  }, [filterName]);

  useEffect(() => {
    getStreamByEndpointAsync(SCHOOL_MONITORING_EVENT_ENDPOINT, setEvents);
  }, []);

  useEffect(() => {
    if (events.length > 0) {
      const mostRecentEvent = events.reduce((latest, current) =>
        transformDateStringInDate(current.createdAt) >
          transformDateStringInDate(latest.createdAt)
          ? current
          : latest
      );
      setLastEvent(mostRecentEvent);
    }
  }, [events]);

  return (
    <Box display="flex" flexDirection="column" height="100%">
      {/* Controles superiores - Layout compacto */}
      <Box
        display="flex"
        flexDirection={{ xs: 'column', md: 'row' }}
        gap={1.5}
        p={2}
        alignItems={{ xs: 'stretch', md: 'center' }}
        flexWrap="wrap"
      >
        {/* Busca */}
        <TextField
          placeholder="Filtrar por nome..."
          value={filterName}
          onChange={(e) => setFilterName(e.target.value)}
          onFocus={() => setFilterFocusState('focused')}
          onBlur={() => setFilterFocusState('default')}
          size="small"
          variant="outlined"
          slotProps={{
            input: {
              endAdornment: filterName && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setIsClearing(true);
                      setFilterName('');
                      setTimeout(() => setIsClearing(false), 300);
                    }}
                    edge="end"
                    tabIndex={-1}
                    sx={{
                      color: isClearing ? '#d32f2f' : filterFocusState === 'focused' ? '#1976d2' : filterFocusState === 'hover' ? '#8c9ead' : '#516f7f',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        color: '#8c9ead',
                      },
                      '&:active': {
                        color: '#d32f2f',
                      },
                      '&.Mui-focusVisible': {
                        color: '#1976d2',
                      },
                    }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
          sx={{
            width: { xs: '100%', md: 250 },
            '& .MuiOutlinedInput-root': {
              height: 40,
            },
          }}
        />

        {/* Filtros por tipo - Multi-select */}
        <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 200 }, height: 40 }}>
          <InputLabel>Tipo de Pessoa</InputLabel>
          <Select
            multiple
            value={selectedTypes}
            label="Tipo de Pessoa"
            onChange={(e) => {
              const value = e.target.value;
              setSelectedTypes(typeof value === 'string' ? value.split(',') as PersonType[] : (value as PersonType[]));
            }}
            onMouseEnter={() => setFilterFocusState('hover')}
            onMouseLeave={() => setFilterFocusState('default')}
            onOpen={() => setFilterFocusState('focused')}
            onClose={() => setFilterFocusState('default')}
            variant="outlined"
            sx={{
              height: 40,
              '& .MuiSelect-icon': {
                color: '#516f7f',
              },
              '&:hover .MuiSelect-icon': {
                color: '#8c9ead',
              },
              '&.Mui-focused .MuiSelect-icon': {
                color: '#1976d2',
              },
            }}
            renderValue={(selected) => {
              if (selected.length === 0) return 'Nenhum selecionado';
              const labels = selected.map((type) => {
                switch (type) {
                  case 'Student':
                    return 'Alunos';
                  case 'Responsible':
                    return 'Responsáveis';
                  case 'General':
                    return 'Funcionários';
                  default:
                    return '';
                }
              });
              return labels.join(', ');
            }}
          >
            <MenuItem value="Student">
              <SchoolIcon sx={{ mr: 1, fontSize: 18 }} />
              Alunos
            </MenuItem>
            <MenuItem value="Responsible">
              <SupervisorAccountIcon sx={{ mr: 1, fontSize: 18 }} />
              Responsáveis
            </MenuItem>
            <MenuItem value="General">
              <BadgeIcon sx={{ mr: 1, fontSize: 18 }} />
              Funcionários
            </MenuItem>
          </Select>
        </FormControl>

        {/* Ordenação */}
        <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 160 }, height: 40 }}>
          <InputLabel>Ordenar</InputLabel>
          <Select
            value={sortBy}
            label="Ordenar"
            onChange={(e) => setSortBy(e.target.value as SortOption)}
            onMouseEnter={() => setFilterFocusState('hover')}
            onMouseLeave={() => setFilterFocusState('default')}
            onOpen={() => setFilterFocusState('focused')}
            onClose={() => setFilterFocusState('default')}
            variant="outlined"
            sx={{
              height: 40,
              '& .MuiSelect-icon': {
                color: '#516f7f',
              },
              '&:hover .MuiSelect-icon': {
                color: '#8c9ead',
              },
              '&.Mui-focused .MuiSelect-icon': {
                color: '#1976d2',
              },
            }}
          >
            <MenuItem value="nameAsc">Nome A-Z</MenuItem>
            <MenuItem value="nameDesc">Nome Z-A</MenuItem>
            <MenuItem value="timeDesc">Mais recente</MenuItem>
            <MenuItem value="timeAsc">Mais antigo</MenuItem>
          </Select>
        </FormControl>

        {/* Botão Atualizar */}
        <Button
          onClick={loadPerimeterStatus}
          disabled={isLoading}
          variant="outlined"
          startIcon={isLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
          sx={{
            minWidth: { xs: '100%', md: 'auto' },
            height: 40,
            whiteSpace: 'nowrap',
            textTransform: 'none',
            fontWeight: 500,
            px: 2,
          }}
        >
          {isLoading ? 'Atualizando...' : 'Atualizar'}
        </Button>
      </Box>

      {/* Cards do perímetro */}
      <Box
        display="flex"
        flex={1}
        height="100%"
        sx={{
          '@media (max-width: 900px)': {
            flexDirection: 'column-reverse',
            p: '15px',
          },
        }}
      >
        <Box
          flex={1}
          display="flex"
          flexDirection="row"
          sx={{
            '@media (max-width: 900px)': {
              flexDirection: 'column',
            },
          }}
        >
          <Box flex={1} ml={2}>
            <CardPerimeterSchool
              title="DENTRO DO COLÉGIO"
              people={peopleInside}
              titleTime="Entrada"
              iconProp={iconSchool}
            />
          </Box>
          <Box flex={1} ml={2}>
            <CardPerimeterSchool
              title="FORA DO COLÉGIO"
              people={peopleOutside}
              titleTime="Saída"
              iconProp={iconOutSideSchool}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default MonitoringSchoolPerimeterPages;
